import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from '../../../utils/toast';
import styled from '@emotion/styled';
import {
  ContentButtonsWrapper,
  TitleAndSubtitle,
} from '../../layout/layout-container.styled';
import { Text, font } from '../../../style-utils/text';
import { palette, radius, shadow, spacing } from '../../../style-utils/theme';
import { arrivedText } from '../../../function-utils/building-time';
import { useClock } from '../../../context/clock-context';
// THE DESK'S OUT LANGUAGE, shared with "Coming out", "Activities out" and the
// parcel shelf. Resident out is the fourth of the four, and it was the one
// still drawing its own row and its own button — see the note where the local
// copies were removed.
import {
  Row,
  RowAction,
  RowLead,
  RowMain,
  RowMark,
  RowMeta,
  RowName,
  Stat,
  Stats,
} from '../visitor-exit/visitor-exit-page.styled';
import { initialsOf } from '../visitor-exit/desk-list';
import { DefaultComponentProps } from '../../app-router/component-props';
import { getBuildingRoster } from '../../../api/hierarchy-client';
import {
  logResidentEntry,
  signVisitorOut,
  VisitorExitError,
} from '../../../api/visitor-log-client';
import { Occupancy, VisitorInside } from '../../../interfaces/hierarchy-interfaces';
import {
  insideCountOf,
  resolvePresence,
  ResidentPresence,
} from './resident-presence';
import { ScanIdButton } from '../../capture/scan-id-button';

/**
 * RESIDENT IN / OUT — the two-tap entry the desk was missing.
 *
 * ── THE GAP ──
 * The visitor-log type picker offered Guest, Prospect, Vendor, Delivery and
 * Emergency, and nothing for the people who actually live in the building. So
 * logging that a resident had come in meant filing them as a "Guest" of their
 * own apartment and walking a five-step wizard — approval, ID, parking
 * validation — every field of which is a question about an outsider. In
 * practice it meant residents were not logged at all.
 *
 * ── WHY THIS IS NOT A WIZARD BRANCH ──
 * Every other type is a wizard because the desk is meeting a stranger and has to
 * collect facts about them. Here the desk already knows everything: the person
 * is on the roster, the roster carries their name and their unit, and the only
 * thing being recorded is a direction. Two taps — who, then in or out — is the
 * whole interaction, and putting it behind five screens is what stopped it being
 * used.
 *
 * ── ONE CALL, BOTH HALVES ──
 * GET /buildings/:id/presence returns the open occupancies AND the open
 * arrivals at a single instant. That is what makes "is this person already
 * signed in?" answerable without a second request that could disagree with the
 * first — and it is what decides whether a row is a sign-in or a sign-out.
 */

const ROSTER_LIMIT = 400;

const nameOf = (person: Occupancy): string =>
  `${person.first_name || ''} ${person.last_name || ''}`.trim();

// `navigate` is deliberately not destructured: back is <BackButton>, which
// routes itself to an explicit path through useNavigate. It was the only caller
// of the prop here.
export const ResidentEntry: React.FC<DefaultComponentProps> = ({
  selectedBuilding,
}) => {
  const buildingId = selectedBuilding ? Number(selectedBuilding.id) : NaN;
  /* A desk screen: "in since" is the BUILDING's clock for whoever is at the desk
     (R-T12), rendered from the instant. */
  const clock = useClock({
    buildingId: Number.isInteger(buildingId) ? buildingId : null,
    buildingZone: selectedBuilding?.timezone,
  });
  // Which half of the menu's Resident card opened this. Absent (a bookmark, a
  // back button) means the full roster, which is the safe default: it can do
  // both jobs, it just does not narrow.
  const [search] = useSearchParams();
  const direction = search.get('direction') === 'out' ? 'out' : 'in';

  const [residents, setResidents] = useState<Occupancy[]>([]);
  const [inside, setInside] = useState<VisitorInside[]>([]);
  const [query, setQuery] = useState('');
  // The name a scan just put in the search box, so the screen can say where the
  // filter came from. Cleared the moment the desk types, because then the
  // filter is theirs and attributing it to a scan would be a lie.
  const [scanned, setScanned] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /* ── PER ROW, NOT ONE ROW AT A TIME ──────────────────────────────────────
     This was a single `busyId: string | null`, and a single value cannot
     describe two requests. Press A then B quickly and setBusyId(B) erased A's
     busy flag while A's request was still in the air: A's button re-enabled
     under the thumb and a second press sent a second write. Then A's `finally`
     ran and cleared the flag for B, which was still in flight, and B could be
     pressed twice as well. A set is the shape the state actually has. */
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set());
  /* THE SAME FACT, READABLE SYNCHRONOUSLY. Two taps inside one React commit
     both see the PRE-state of `busy` — setState is not a variable assignment —
     so the state above disables the button and this ref is what refuses the
     second call. Both are needed and neither is redundant. */
  const inFlight = useRef<Set<string>>(new Set());
  const [error, setError] = useState('');

  /* ── THE STALE READ ──────────────────────────────────────────────────────
     Every sign-in and sign-out ends in load(), and a building change starts
     one too, so several reads are routinely in the air at once. Without a
     sequence number the LAST RESPONSE wins rather than the LATEST REQUEST, and
     an older answer that overtakes a newer one puts a person the desk has just
     signed out back on the screen as inside — with a stale arrival id behind
     the button, which the server then refuses as ALREADY_DEPARTED. The desk
     sees a dead button on a row that says somebody is here. */
  const readSeq = useRef(0);

  const load = useCallback(async () => {
    if (!Number.isInteger(buildingId)) {
      setLoading(false);

      return;
    }
    const seq = ++readSeq.current;
    try {
      const roster = await getBuildingRoster(buildingId);
      if (seq !== readSeq.current) return;
      setResidents(roster.residents || []);
      // null means "you may not see visitor rows" — a different answer from an
      // empty list, and the reason this is not `|| []` on the outer value.
      setInside(roster.visitors_inside || []);
      setError('');
    } catch {
      if (seq !== readSeq.current) return;
      setError('We could not load the roster for this building.');
    } finally {
      if (seq === readSeq.current) setLoading(false);
    }
  }, [buildingId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * WHICH OPEN ARRIVAL BELONGS TO WHICH RESIDENT.
   *
   * This was a Map<unit_id, VisitorInside> built first-row-wins, so every
   * resident of a two-person household resolved to the SAME arrival and signing
   * one of them out closed the other's row. The whole argument, and the three
   * outcomes that replace it, are in resident-presence.ts — a separate module
   * because a render assertion cannot tell "closed the right row" from "closed
   * a row", and this is the thing that has to be tested directly.
   *
   * The unit still NARROWS (visitor names are AES-GCM, so no server predicate
   * can match one and the unit is the only foreign key); the name now
   * DISAMBIGUATES inside it.
   */
  const presence = useMemo(
    () => resolvePresence(residents, inside),
    [residents, inside]
  );
  const presenceOf = useCallback(
    (person: Occupancy): ResidentPresence =>
      presence.get(person.id) || { entry: null, state: 'out' },
    [presence]
  );
  /* THE ARRIVALS NOBODY ON THE ROSTER CLAIMED.
     resolvePresence hands each open arrival to at most one resident, so what is
     left over is a real number: rows that exist, are open, and cannot be closed
     from THIS screen without guessing whose stay is ending. They are not hidden
     — the count is stated and the reader is sent to the screen that lists
     arrivals as themselves. */
  const unmatchedOpen = useMemo(() => {
    const claimed = new Set<number>();
    presence.forEach(p => {
      if (p.entry) claimed.add(p.entry.id);
    });

    return inside.filter(
      v =>
        v.visitor_type === 'Resident Entry' &&
        v.unit_id != null &&
        !claimed.has(v.id)
    ).length;
  }, [inside, presence]);

  // HOW MANY RESIDENTS ARE ACTUALLY IN, whatever the search box currently
  // shows. Counted off the RESOLVED arrivals rather than off "this unit has an
  // open row", so a household with two people and one logged entry reports one
  // person inside instead of two. Not `visible`, so typing a name narrows the
  // list without appearing to empty the building.
  const insideCount = useMemo(() => insideCountOf(presence), [presence]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    /* THE CAP IS APPLIED LAST, AFTER SEARCHING — NOT FIRST.
     *
     * `residents.slice(0, ROSTER_LIMIT)` used to run before the filter, so in a
     * building with more than 400 occupancies nobody past index 400 could be
     * found AT ALL: the desk typed a resident's name, the list stayed empty,
     * and the screen offered no hint that the roster had been truncated before
     * the search ever saw it. The cap exists to bound what is RENDERED, which
     * is a rendering concern and belongs after the question has been asked. */
    let rows = residents;

    // ── ?direction=out — ONLY THE PEOPLE WHO ARE ACTUALLY IN ────────────────
    // The menu's Resident card carries In and Out, and two halves that opened
    // the same undifferentiated list would be a decorated single button. Out is
    // the question "who is here that I can sign out", and the answer is a
    // handful of people, not the whole building — so the desk stops hunting one
    // name in four hundred. In stays the full roster, because anybody who lives
    // here can walk in.
    //
    // A FILTER, NOT A MODE: the row still decides what it offers, so a resident
    // who is out cannot be signed out from either screen, and nothing here can
    // record a direction that contradicts the presence data.
    if (direction === 'out') {
      // WHO CAN ACTUALLY BE SIGNED OUT, which is people with an arrival RESOLVED
      // TO THEM — not "somebody in this unit is in". Those are different sets
      // the moment a household has two people, and using the second one is how
      // the wrong person's stay got closed.
      rows = rows.filter(person => !!presenceOf(person).entry);
    }
    if (needle) {
      rows = rows.filter(
        person =>
          nameOf(person).toLowerCase().includes(needle) ||
          String(person.unit_number || '')
            .toLowerCase()
            .includes(needle)
      );
    }

    return rows.slice(0, ROSTER_LIMIT);
  }, [residents, query, direction, presenceOf]);

  /* How many the cap is holding back, so the count below can say so rather than
     letting the desk believe they are looking at everyone. */
  const hiddenByCap = useMemo(() => {
    const needle = query.trim().toLowerCase();
    let matched = residents;

    if (direction === 'out') {
      matched = matched.filter(person => !!presenceOf(person).entry);
    }
    if (needle) {
      matched = matched.filter(
        person =>
          nameOf(person).toLowerCase().includes(needle) ||
          String(person.unit_number || '')
            .toLowerCase()
            .includes(needle)
      );
    }

    return Math.max(matched.length - ROSTER_LIMIT, 0);
  }, [residents, query, direction, presenceOf]);

  /* ── ONE WRITE PER ROW, EVEN UNDER A FAST THUMB ──────────────────────────
     The ref is the gate and the state is the paint. `inFlight` is read and
     written synchronously, so two presses inside a single React commit — which
     is what "clicked too fast" produces — hit the same Set and the second one
     returns before it can send anything. The state exists so the button also
     LOOKS unavailable; it cannot do the gating, because a second handler in the
     same commit still sees the pre-update value. */
  const begin = (key: string): boolean => {
    if (inFlight.current.has(key)) return false;
    inFlight.current.add(key);
    setBusy(prev => new Set(prev).add(key));

    return true;
  };
  const end = (key: string): void => {
    inFlight.current.delete(key);
    setBusy(prev => {
      const next = new Set(prev);
      next.delete(key);

      return next;
    });
  };

  const signIn = async (person: Occupancy) => {
    if (!begin(person.id)) return;
    try {
      await logResidentEntry({
        property_ids: [buildingId],
        unit_id: person.unit_id,
        first_name: person.first_name || '',
        last_name: person.last_name || '',
      });
      toast.success(`${nameOf(person) || 'Resident'} signed in`);
      await load();
    } catch {
      toast.error('Could not record that entry.');
    } finally {
      end(person.id);
    }
  };

  const signOut = async (person: Occupancy, entry: VisitorInside) => {
    /* KEYED ON THE ARRIVAL, NOT ON THE PERSON. Two residents of one unit are two
       keys and must not block each other; the same arrival reached twice is one
       key and must. That is also the post-condition resolvePresence guarantees —
       an arrival is claimed by at most one resident — so this key is unique per
       row on the screen as well. */
    const key = `exit:${entry.id}`;
    if (!begin(key)) return;
    try {
      await signVisitorOut(entry.id);
      toast.success(`${nameOf(person) || 'Resident'} signed out`);
      await load();
    } catch (e) {
      const err = e as VisitorExitError;
      toast.error(err.message || 'Could not record that departure.');
      // Re-read either way: an ALREADY_DEPARTED means this screen is stale, and
      // leaving it stale would offer the same dead button again.
      await load();
    } finally {
      end(key);
    }
  };

  return (
    <>
      {/* THE BACK BUTTON, TOP LEFT — the product's one design and one position
          (components/layout/back-button.tsx). It replaced a bottom-left
          <NavButton direction="back">, which is the WIZARD's step control: this
          screen is opened straight from the menu's Resident card and is not a
          wizard step, so leaving it and stepping back inside a flow looked
          identical and sat in the same corner. */}
      {/* The BackBar that used to sit here is now rendered once by
          WizardLayout, inside StepPane (WP-10). */}
      <TitleAndSubtitle direction="column">
        <Text as="h1" fontSize={font.size.xl} fontWeight={700} mt={0} mb={8}>
          {direction === 'out' ? 'Sign a resident out' : 'Sign a resident in'}
        </Text>
        <Text color={font.color.gray} fontSize={font.size.xs} mt={0} mb={0}>
          {direction === 'out'
            ? 'Everyone currently signed in. Tap once to sign them out.'
            : 'Find them on the roster and tap once. No ID, no approval — they live here.'}
        </Text>
      </TitleAndSubtitle>

      <ContentButtonsWrapper>
        {/* ── THE COUNTS, ON THE OUT HALF ONLY ─────────────────────────────
            Out is the question "who is here that I can sign out", and the
            answer is a handful of people — so how many there are, and how many
            of them the search is currently showing, are both worth a glance.
            IN is the whole roster: a tile reading "412" would be a fact about
            the building's size, not about anything happening at the desk. */}
        {direction === 'out' && !loading && (
          <Stats>
            <Stat tone={insideCount ? 'brand' : 'plain'}>
              <b>{insideCount}</b>
              <span>Signed in now</span>
            </Stat>
            <Stat>
              <b>{visible.length}</b>
              {/* SAYS WHEN IT IS NOT SHOWING EVERYTHING. A truncated list that
                  reports its own length as though it were the answer is how a
                  desk concludes somebody does not live here. */}
              <span>
                {hiddenByCap > 0
                  ? `Showing (${hiddenByCap} more — narrow the search)`
                  : query.trim()
                    ? 'Matching'
                    : 'Showing'}
              </span>
            </Stat>
            <Stat>
              <b>{residents.length}</b>
              <span>On the roster</span>
            </Stat>
          </Stats>
        )}
        {/* ── THE ARRIVALS THIS SCREEN WILL NOT GUESS AT ────────────────────
            Stated rather than hidden. An open Resident Entry that no roster
            name claims is a real open stay; it is simply not closable from a
            list of PEOPLE without deciding whose it is, and deciding that
            wrongly is the reported defect. The number is the honest thing to
            show, with the screen that can close them named. */}
        {direction === 'out' && !loading && unmatchedOpen > 0 && (
          <UnmatchedNote role="status">
            {unmatchedOpen === 1
              ? '1 open resident arrival does not match anybody on this roster by name, so it is not listed above. Close it from Coming out, where arrivals are listed as themselves.'
              : `${unmatchedOpen} open resident arrivals do not match anybody on this roster by name, so they are not listed above. Close them from Coming out, where arrivals are listed as themselves.`}
          </UnmatchedNote>
        )}
        <Search
          type="search"
          value={query}
          placeholder="Search a name or a unit"
          aria-label="Search the roster"
          onChange={e => {
            setQuery(e.target.value);
            setScanned(null);
          }}
        />

        {/* ── THE SCAN, ON BOTH DIRECTIONS ────────────────────────────────
            Every other screen where a person stands at the desk offers one —
            the visitor wizard, the exit desk, the parcel shelf — and this was
            the one that did not, even though a resident who has lost their fob
            and is standing there with a passport is exactly the case it helps.

            IT FILLS THE SEARCH, IT DOES NOT SIGN ANYBODY IN. The row is still
            what records the movement, so a misread name cannot silently mark
            the wrong neighbour as home, and the desk sees who it matched before
            anything is written. That is also why it works unchanged for Out:
            narrowing a list is direction-agnostic. */}
        <ScanRow>
          <ScanIdButton
            mode="id"
            buildingId={Number.isInteger(buildingId) ? buildingId : null}
            label="Scan ID"
            disabled={!Number.isInteger(buildingId)}
            onResult={r => {
              const name = `${r.first_name || ''} ${r.last_name || ''}`.trim();
              if (!name) return;
              setQuery(name);
              setScanned(name);
            }}
          />
        </ScanRow>

        {scanned && (
          <ScanNote>
            Scanned <strong>{scanned}</strong> — showing the roster matches.
            <ScanClear type="button" onClick={() => { setScanned(null); setQuery(''); }}>
              clear
            </ScanClear>
          </ScanNote>
        )}

        {loading ? (
          <Empty>Loading the roster…</Empty>
        ) : error ? (
          <Empty role="alert">{error}</Empty>
        ) : !Number.isInteger(buildingId) ? (
          <Empty>Choose a building first.</Empty>
        ) : visible.length === 0 ? (
          <Empty>
            {/* THE OUT LIST IS EMPTY FOR ITS OWN REASON, and saying "nobody
                matches that" when nothing was typed is how a working screen
                reads as broken. */}
            {scanned
              ? `Nobody on the roster is named ${scanned}. Search by unit, or clear the scan.`
              : direction === 'out' && !query.trim()
              ? 'Nobody is signed in right now.'
              : residents.length
              ? 'Nobody on the roster matches that.'
              : 'Nobody is recorded as living in this building yet.'}
          </Empty>
        ) : (
          visible.map(person => {
            const { entry, state } = presenceOf(person);
            /* ONE VALUE, ONE JOB. `entry` is THIS person's arrival and is what
               the button acts on; the tone follows it. The old code had a single
               unit-keyed value doing both, and closed the wrong row to do it. */
            const anyoneIn = !!entry;
            const rowBusy =
              busy.has(person.id) || (!!entry && busy.has(`exit:${entry.id}`));
            const label = nameOf(person) || 'Unnamed resident';

            return (
              /* `picked` when they are currently inside: the mark carries the
                 state at the left edge, where the eye enters the row, so the
                 out list is scannable without reading a word of it. */
              <Row key={person.id} tone={anyoneIn ? 'picked' : 'plain'}>
                <RowLead>
                  <RowMark tone={anyoneIn ? 'picked' : 'plain'} aria-hidden="true">
                    {initialsOf(label)}
                  </RowMark>
                  <RowMain>
                    <RowName>{label}</RowName>
                    <RowMeta>
                      {person.unit_number
                        ? `Unit ${person.unit_number}`
                        : 'No unit recorded'}
                      {entry ? ` · in since ${arrivedText(entry, clock.zone)}` : ''}
                      {/* SAYS WHAT THE ROW IS NOT CLAIMING. The unit has more
                          than one open arrival and none of them names this
                          person, so this screen will not guess which stay a
                          sign-out here would end — that guess is the defect it
                          was reported for. Signing them IN is still offered and
                          is still correct: no open arrival carries their name.
                          "Coming out" lists arrivals as themselves and closes
                          the right one. */}
                      {state === 'ambiguous'
                        ? ' · this unit has other open arrivals — close those from Coming out'
                        : ''}
                    </RowMeta>
                  </RowMain>
                </RowLead>
                {/* IN AND OUT DO NOT LOOK THE SAME — the rule the local `Action`
                    button existed for, kept: they are opposite records of a
                    person's whereabouts, they sit on the same row, and a desk
                    moving fast will press whichever is under their thumb. Sign
                    IN is the solid brand press; signing somebody OUT closes a
                    stay and is the quiet one. */}
                <RowAction
                  type="button"
                  variant={entry ? 'quiet' : 'primary'}
                  disabled={rowBusy}
                  onClick={() =>
                    entry ? signOut(person, entry) : signIn(person)
                  }
                >
                  {rowBusy ? '…' : entry ? 'Sign out' : 'Sign in'}
                </RowAction>
              </Row>
            );
          })
        )}
      </ContentButtonsWrapper>
    </>
  );
};

/* The scan sits under the search box: both do the same job — narrow the roster
   to one person — so they belong together, and the button is the faster of the
   two when somebody is holding a document. */
const ScanRow = styled.div`
  display: flex;
  margin-bottom: ${spacing.sm};
`;

const ScanNote = styled.p`
  margin: 0 0 ${spacing.sm};
  font-family: ${font.family};
  font-size: ${font.size.xxs}px;
  color: ${palette.slate};
`;

/* The same quiet note as ScanNote, tinted amber: it reports something the desk
   has to go and do elsewhere, which is not the same weight as saying where a
   filter came from. No icon and no border — it sits above a list and must not
   read as an error. */
const UnmatchedNote = styled.p`
  margin: 0 0 ${spacing.sm};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.warningSoft};
  color: ${palette.ink};
  font-family: ${font.family};
  font-size: ${font.size.xxs}px;
  line-height: 1.45;
`;

const ScanClear = styled.button`
  margin-left: ${spacing.xs};
  padding: 0;
  border: none;
  background: none;
  color: ${palette.lagoon};
  font-family: ${font.family};
  font-size: ${font.size.xxs}px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
`;

const Search = styled.input`
  width: 100%;
  padding: ${spacing.xs} ${spacing.sm};
  margin-bottom: ${spacing.sm};
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.sm};
  font-family: ${font.family};
  font-size: ${font.size.xs}px;
  color: ${palette.ink};
  background: ${palette.surface};

  &:focus {
    /* OUTLINE NONE, WITH NOTHING PUT BACK. Across 821 audited screens and every
       Tab press on each, this was the ONE control in the product with no focus
       indicator at all — a border tint is not one, and it is invisible to
       anyone who cannot see colour. shadow.focus is what every other input in
       the app pairs with a suppressed outline.
       NO BACKTICKS IN THIS COMMENT: it is inside a styled template literal. */
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

/* Row, RowMain, RowName, RowMeta and Action USED TO BE DECLARED HERE — a fourth
   private description of the same list a desk reads on four screens. They came
   from visitor-exit-page.styled now (see the import), which is where the other
   three already got them. The rule the local `Action` carried is not lost: it
   is the `variant` the row passes, and the comment moved with it. */

const Empty = styled.div`
  padding: ${spacing.lg};
  text-align: center;
  font-family: ${font.family};
  font-size: ${font.size.xs}px;
  color: ${palette.slate};
`;
